import React, { useEffect } from "react"
import { Box, Grid, Typography } from "@mui/material"
import { AutocompleteSeeker } from "@ui-seeker/core"

import { ADMIN_ROUTES } from "@constants"
import { AppFilterPanel, AppNote, filterPanelGridSx } from "@components"
import { dsColorNew } from "@palette"
import PageWrapper from "Pages/@PageWrapper"
import useConfirm from "Pages/DsRepricing/@components/useConfirm"
import { fetchNewsAnnouncements, fetchNewsAudiences } from "./@hooks"
import { clearNewsManagementSignals, isLoadingNews, newsAudiences, newsFilters, newsListFailed } from "./@signals"
import NewsManagementEditor from "./@components/Editor"
import NewsManagementTable from "./@components/Table"


export default function NewsManagementPage() {
	const [confirm, confirmDialog] = useConfirm()

	useEffect(() => {
		// audiences first: the listing's rows carry the audience names, and the table only repaints
		// when its rows change — an audience list landing after them left every name "Unknown user"
		// the skeleton from the first moment: while the audiences arrive the table would read
		// "No announcements yet" before the listing lands
		isLoadingNews.value = true
		fetchNewsAudiences().finally(fetchNewsAnnouncements)
		return clearNewsManagementSignals
	}, [])

	const setFilter = (field, value) => {
		newsFilters.value = { ...newsFilters.value, [field]: value ?? null }
		fetchNewsAnnouncements()
	}

	const { companies, users } = newsAudiences.value

	return <PageWrapper
		head={`DS Rentacar | ${ADMIN_ROUTES.TITLE}`}
		title={ADMIN_ROUTES.TITLE}
		subtitle={ADMIN_ROUTES.NEWS_MANAGEMENT_SUBTITLE}
		isEmpty={false}
	>
		<AppFilterPanel>
			<Box sx={filterPanelGridSx(4)}>
				<AutocompleteSeeker
					label="Company"
					placeholder="Any company"
					disableClearable={false}
					options={companies.map(c => ({ value: c.company, label: c.company }))}
					value={newsFilters.value.company}
					onChange={e => setFilter('company', e.target.value)}
				/>
				<AutocompleteSeeker
					label="User"
					placeholder="Any user"
					disableClearable={false}
					options={users.map(u => ({ value: u.uid, label: u.username }))}
					value={newsFilters.value.uid}
					onChange={e => setFilter('uid', e.target.value)}
				/>
			</Box>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1.5 }}>
				Filtering by company or user also keeps the announcements addressed to everyone, since those reach them too.
			</Typography>
		</AppFilterPanel>

		<Grid container spacing={2}>
			{newsListFailed.value && <Grid item xs={12}>
				<AppNote severity="error">
					The announcements could not be loaded.{' '}
					<Box component="span" onClick={fetchNewsAnnouncements} sx={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>Try again</Box>
				</AppNote>
			</Grid>}
			<NewsManagementTable confirm={confirm}/>
		</Grid>

		<NewsManagementEditor/>
		{confirmDialog}
	</PageWrapper>
}
